import { useEffect } from 'react'
import type { Theme } from '@mui/material/styles'

/** The top-bar button: an icon button, as tall as the avatar beside it (px). */
export const ASSISTANT_ASK_BAR_SIZE = 40

/** The top-bar field the circle opens into on wide screens (px). */
export const ASSISTANT_ASK_BAR_WIDTH = 340

/** The docked panel's width from sm up (px); full width on phones. */
export const ASSISTANT_PANEL_WIDTH = 400

/** The docked panel's id, for the top-bar button's aria-controls. */
export const ASSISTANT_PANEL_ID = 'assistant-panel'

/**
 * The docked panel on wider screens: above the app bar (1000) and page
 * content, below the app's drawers (1200) and modals (1300), so a filter or
 * notifications drawer still covers it. Fixed bottom chrome such as the case
 * page's action bar is avoided with the bottom inset, not stacking.
 */
export const getAssistantZIndex = (theme: Theme) => theme.zIndex.appBar + 1

/**
 * The open panel. Full width on phones, where it must cover the case page's
 * mobile action bar (1200) and the bottom nav; nothing behind it can be
 * reached there, so no drawer can open over it. Docked on wider screens: the
 * level above.
 */
export const getAssistantPanelZIndex = (theme: Theme) => ({
  xs: theme.zIndex.drawer + 1,
  sm: getAssistantZIndex(theme),
})

/**
 * CSS variable holding the height (px) of fixed chrome along the bottom edge
 * on tablets: the mobile bottom nav, or the case page's mobile action bar.
 * The docked panel stops that much higher from sm up; on phones it covers
 * them.
 */
export const ASSISTANT_BOTTOM_INSET_VAR = '--assistant-bottom-inset'

/** `calc()` expression: the bottom inset plus `px`. */
export const aboveBottomInset = (px: number) =>
  `calc(var(${ASSISTANT_BOTTOM_INSET_VAR}, 0px) + ${px}px)`

/**
 * Reserve `height` px at the bottom of the viewport for the docked panel
 * while the caller is mounted. 0 reserves nothing (e.g. the chrome is hidden
 * on md+) and leaves another caller's value alone.
 */
export const useAssistantBottomInset = (height: number) => {
  useEffect(() => {
    if (height <= 0) return undefined
    const root = document.documentElement
    root.style.setProperty(ASSISTANT_BOTTOM_INSET_VAR, `${height}px`)
    return () => {
      root.style.removeProperty(ASSISTANT_BOTTOM_INSET_VAR)
    }
  }, [height])
}
